import {
  Button,
  FormInput,
  FormListbox,
  FormListBoxItem,
} from '@wwtr/components/ui';
import { memo, PropsWithChildren, useCallback, useEffect } from 'react';
import { OrganisationsSearchOptionsQuery } from '@wwtr/generated';
import { useSearchFilters } from '@wwtr/libs/contexts';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import cn from 'classnames';

export type OrganizationListingFilterBarProps = PropsWithChildren<{
  data: OrganisationsSearchOptionsQuery;
  isLoading: boolean;
  onSubmit: () => void;
}>;

const searchValidationSchema = Yup.object().shape({
  query: Yup.string().max(30, 'Zoekterm is te lang'),
  postalCode: Yup.string().matches(/\d{4}[ ]?[A-Z]{2}/g, 'Ongeldige postcode'),
});

export const OrganizationListingFilters = memo(
  ({ data, isLoading, onSubmit }: OrganizationListingFilterBarProps) => {
    const {
      filters,
      addFilter,
      removeFilter,
      resetFilters,
      getFilterValue,
      getFilterSingleValue,
    } = useSearchFilters();

    const setOrganizationCategoryFilter = useCallback(
      (categoryId: string) => {
        if (categoryId) {
          const addedCategory = data.organizationCategories.find(
            category => category.id === categoryId
          );

          const label = addedCategory.title ?? 'Onbekend';

          addFilter('organizationCategory', categoryId, label, true);
        } else {
          removeFilter('organizationCategory');
        }
      },
      [addFilter, removeFilter, data]
    );

    const setServiceCategoryFilter = useCallback(
      (categoryId: string) => {
        if (categoryId) {
          const addedCategory = data.serviceCategories.find(
            category => category.id === categoryId
          );

          const label = addedCategory.title ?? 'Onbekend';

          addFilter('serviceCategory', categoryId, label, true);
        } else {
          removeFilter('serviceCategory');
        }
      },
      [addFilter, removeFilter, data]
    );

    const setRadiusFilter = useCallback(
      (radius: string) => {
        addFilter('radius', radius, `${radius}km`, true);
      },
      [addFilter]
    );

    const searchForm = useFormik({
      initialValues: {
        query: '',
        postalCode: '',
        radius: '',
        organizationCategory: '',
        serviceCategory: '',
      },
      validationSchema: searchValidationSchema,
      onSubmit: values => {
        Object.entries(values).map(([name, value]) => {
          if (value) {
            if (name === 'postalCode') {
              addFilter(name, value, `Postcode: ${value}`, true);
              addFilter('radius', '20', `20km`, true);
            } else {
              addFilter(name, value, value, true);
            }
          } else {
            removeFilter(name, value);
          }
        });

        onSubmit();
      },
    });

    useEffect(() => {
      searchForm.setFieldValue('query', getFilterSingleValue('query') ?? '');
      searchForm.setFieldValue(
        'postalCode',
        getFilterSingleValue('postalCode') ?? ''
      );
      searchForm.setFieldValue('radius', getFilterSingleValue('radius') ?? '');
      searchForm.setFieldValue(
        'organizationCategory',
        getFilterSingleValue('organizationCategory') ?? ''
      );
      searchForm.setFieldValue(
        'serviceCategory',
        getFilterSingleValue('serviceCategory') ?? ''
      );
    }, [filters]);

    const resetForm = useCallback(() => {
      searchForm.resetForm();
      resetFilters();
    }, []);

    const emptyListBoxItem: FormListBoxItem = {
      id: null,
      value: null,
      name: 'Selecteer',
    };

    const organizationCategoryListBoxData: FormListBoxItem[] = [
      emptyListBoxItem,
    ].concat(
      data.organizationCategories?.map(category => ({
        id: category.id,
        value: category.id,
        name: category.title,
      }))
    );

    const serviceCategoryListBoxData: FormListBoxItem[] = [
      emptyListBoxItem,
    ].concat(
      data.serviceCategories?.map(category => ({
        id: category.id,
        value: category.id,
        name: category.title,
      }))
    );

    if (isLoading) return null;

    return (
      <div className="flex flex-col">
        <form onSubmit={searchForm.handleSubmit} className="flex flex-col">
          <div className="border-b border-b-gray p-8 pt-4">
            <label
              className={cn('text-xs', {
                'text-gray-700': !searchForm.errors.query,
                'text-red-700': searchForm.errors.query,
              })}
              htmlFor="query"
            >
              {searchForm.errors.query ? searchForm.errors.query : 'Zoekterm'}
            </label>
            <FormInput
              name="query"
              placeholder=""
              onChange={searchForm.handleChange}
              value={searchForm.values.query}
            />
          </div>
          <div className="flex flex-row border-b border-gray-100 p-8 pt-4">
            <div className="w-32 mr-8">
              <label
                className={cn('text-xs', {
                  'text-gray-700': !searchForm.errors.postalCode,
                  'text-red-700': searchForm.errors.postalCode,
                })}
                htmlFor="postalCode"
              >
                {searchForm.errors.postalCode
                  ? searchForm.errors.postalCode
                  : 'Postcode'}
              </label>
              <FormInput
                name="postalCode"
                placeholder=""
                onChange={searchForm.handleChange}
                value={searchForm.values.postalCode}
              />
            </div>
            <div className="w-32 relative">
              <label className="text-xs text-gray-700" htmlFor="radius">
                Afstand
              </label>
              <FormListbox
                data={[10, 20, 30, 50, 100].map<FormListBoxItem>(radius => ({
                  id: radius.toString(),
                  value: radius.toString(),
                  name: `${radius}km`,
                }))}
                value={searchForm.values.radius}
                onChange={setRadiusFilter}
              />
            </div>
          </div>

          <div className="border-b border-b-gray p-8 pt-4">
            <label className="text-xs text-gray-700" htmlFor="postalCode">
              Soort organisatie
            </label>
            <FormListbox
              data={organizationCategoryListBoxData}
              value={searchForm.values.organizationCategory}
              onChange={setOrganizationCategoryFilter}
            />
          </div>

          <div className="border-b border-b-gray p-8 pt-4">
            <label className="text-xs text-gray-700" htmlFor="postalCode">
              Soort aanbod
            </label>
            <FormListbox
              data={serviceCategoryListBoxData}
              value={searchForm.values.serviceCategory}
              onChange={setServiceCategoryFilter}
            />
          </div>

          <div className="flex flex-row items-center p-8 pt-4">
            <Button
              type="submit"
              color="powerblue"
              appearance="solid"
              size="sm"
            >
              Filters toepassen
            </Button>

            {filters.size > 0 && (
              <Button
                color="hotpink"
                appearance="outline"
                size="sm"
                className="ml-2"
                onClick={resetForm}
              >
                Filters wissen
              </Button>
            )}
          </div>
        </form>
      </div>
    );
  }
);

OrganizationListingFilters.displayName = 'OrganizationListingFilterBar';
